import dayjs from "dayjs";
import {
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import Loader from "components/lists/Loader";
import { ADDITIONAL_ITEMS_IN_MOVEMENTS_URL } from "constants/urls";
import useCrud from "hooks/useCrud";
import { useEffect, useMemo, useState } from "react";
import { Liquidation, Movement } from "types";
import AdditionalItemRow from "./AdditionalItemRow";
import DischargesPage from "pages/DischargesPage";

export default function AdditionalsModal({
  open,
  liquidation,
  onClose,
  onLiquidationCreate,
  onAdditionalItemChange,
  onMovementChange,
  extraMovement,
}: {
  open: boolean;
  liquidation: Liquidation;
  onClose: () => void;
  onLiquidationCreate: () => void;
  onAdditionalItemChange: (additional_item: {
    id: number;
    description: string;
    price: number;
  }) => void;
  onMovementChange: (movement: Movement) => void;
  extraMovement: Movement | undefined;
}) {
  const { client_id, store_id, date_from, date_to } = liquidation;
  const [currentTab, setCurrentTab] = useState<number>(0);

  const {
    data: additional_items,
    loading,
    fetchData: getAdditionalItems,
  } = useCrud<{ id: number; description: string; price: number }>(
    ADDITIONAL_ITEMS_IN_MOVEMENTS_URL,
    { disableAutoFetch: true }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    getAdditionalItems(undefined, true, {
      clientId: client_id,
      storeId: store_id,
      fromDate: date_from,
      toDate: date_to,
    });
  }, [liquidation]);

  const minDate = useMemo(() => {
    const tomorrow = dayjs().add(1, "day").toDate();
    if (!date_from) return tomorrow;
    const dateFrom = new Date(date_from);
    return dateFrom > tomorrow ? dateFrom : tomorrow;
  }, [dayjs, date_from]);

  const maxDate = useMemo(() => {
    if (date_to) {
      return new Date(date_to);
    }
    return undefined;
  }, [dayjs, date_to]);

  const isExtraMovementValid = useMemo(() => {
    if (extraMovement) {
      if (
        extraMovement.updated_by ||
        extraMovement.date ||
        extraMovement.associated_client ||
        extraMovement.client_document ||
        extraMovement.pallets ||
        extraMovement.weight ||
        extraMovement.numero_remito ||
        extraMovement.observation ||
        extraMovement.additional_items.length > 0 ||
        extraMovement.articles.length > 0
      ) {
        return (
          extraMovement.updated_by &&
          extraMovement.date &&
          extraMovement.associated_client &&
          extraMovement.client_document &&
          extraMovement.pallets &&
          extraMovement.weight &&
          extraMovement.numero_remito &&
          (extraMovement.additional_items.length > 0 ||
            extraMovement.articles.length > 0)
        );
      }
    }
    return true;
  }, [extraMovement]);

  return (
    <Modal
      open={open}
      sx={{
        position: "absolute",
        top: "5%",
        left: "5%",
        overflow: "scroll",
        overflowX: "hidden",
        display: "block",
        marginRight: "5%",
      }}
    >
      <Paper
        sx={{
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          height: "auto",
        }}
      >
        {loading && <Loader isFirstLoad={false} isLoading={loading} />}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="Egreso" />
                <Tab label="Ítems Adicionales" />
              </Tabs>
            </Box>
          </Grid>

          {currentTab === 0 ? (
            <>
              <Grid item xs={12}>
                <DischargesPage
                  isForLiquidation
                  clientId={liquidation.client_id || undefined}
                  storeId={liquidation.store_id || 0}
                  minDate={minDate}
                  maxDate={maxDate}
                  onMovementChange={onMovementChange}
                  extraMovement={extraMovement}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="h4" fontWeight={"bold"}>
                  ¿Desea agregar ítems adicionales a la liquidación?
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper} sx={{ maxHeight: "20rem" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow key={"head"}>
                        <TableCell style={{ textAlign: "center" }}>
                          Descripcion
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          Precio
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {additional_items.map((ai, index) => (
                        <AdditionalItemRow
                          additional_item={ai}
                          key={index}
                          onChange={onAdditionalItemChange}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              width={"100%"}
              display={"inline-flex"}
              justifyContent={"right"}
              gap={2}
            >
              <Button
                variant="outlined"
                onClick={onLiquidationCreate}
                disabled={!isExtraMovementValid}
              >
                Crear Liquidacion
              </Button>
              <Button variant="outlined" onClick={() => onClose()}>
                Cancelar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
